/**
 * ThemeContext - Globalny kontekst ustawień wyglądu (skin)
 * Aplikuje zmienne CSS na podstawie ustawień z serwera
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const ThemeContext = createContext(null);

// Mapowanie wartości na zmienne CSS
const CSS_MAPPINGS = {
  // Czcionka
  font_family: {
    'Inter': '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'Roboto': '"Roboto", sans-serif',
    'Open Sans': '"Open Sans", sans-serif',
    'Lato': '"Lato", sans-serif',
    'Poppins': '"Poppins", sans-serif',
    'Source Sans Pro': '"Source Sans Pro", sans-serif',
    'Nunito': '"Nunito", sans-serif',
    'Montserrat': '"Montserrat", sans-serif'
  },

  font_size_base: {
    '12': '12px',
    '13': '13px',
    '14': '14px',
    '15': '15px',
    '16': '16px',
    '18': '18px'
  },

  font_size_headings: {
    'small': '0.9',
    'normal': '1',
    'large': '1.15'
  },

  // Gęstość UI
  ui_density: {
    'compact': { padding: '0.25rem', gap: '0.5rem', spacing: '0.75rem' },
    'normal': { padding: '0.5rem', gap: '0.75rem', spacing: '1rem' },
    'comfortable': { padding: '0.75rem', gap: '1rem', spacing: '1.25rem' }
  },

  // Zaokrąglenia
  border_radius: {
    'none': '0',
    'small': '0.25rem',
    'medium': '0.5rem',
    'large': '0.75rem',
    'full': '1rem'
  },

  // Rozmiar przycisków
  button_size: {
    'small': { height: '32px', padding: '0.25rem 0.75rem', fontSize: '0.8rem' },
    'medium': { height: '38px', padding: '0.5rem 1rem', fontSize: '0.875rem' },
    'large': { height: '44px', padding: '0.625rem 1.25rem', fontSize: '1rem' }
  },

  // Gęstość tabel
  table_density: {
    'compact': { cellPadding: '0.25rem 0.5rem', rowHeight: '32px' },
    'normal': { cellPadding: '0.5rem 0.75rem', rowHeight: '40px' },
    'comfortable': { cellPadding: '0.75rem 1rem', rowHeight: '48px' }
  }
};

// Domyślne wartości
const DEFAULT_SETTINGS = {
  font_family: 'Inter',
  font_size_base: '14',
  font_size_headings: 'normal',
  theme_mode: 'light',
  primary_color: 'var(--info)',
  sidebar_color: '#1E293B',
  ui_density: 'normal',
  border_radius: 'medium',
  button_size: 'medium',
  table_density: 'normal',
  animations_enabled: 'true',
  sidebar_collapsed: 'false'
};

export function ThemeProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  // Pobierz ustawienia z serwera
  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/theme-settings/css-variables');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // theme settings fetch failed, using defaults
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplikuj zmienne CSS do dokumentu
  const applyTheme = useCallback((newSettings) => {
    const root = document.documentElement;

    // Czcionka
    if (newSettings.font_family && CSS_MAPPINGS.font_family[newSettings.font_family]) {
      root.style.setProperty('--font-family', CSS_MAPPINGS.font_family[newSettings.font_family]);
    }

    // Rozmiar bazowy
    if (newSettings.font_size_base && CSS_MAPPINGS.font_size_base[newSettings.font_size_base]) {
      root.style.setProperty('--font-size-base', CSS_MAPPINGS.font_size_base[newSettings.font_size_base]);
    }

    // Nagłówki - mnożnik
    if (newSettings.font_size_headings && CSS_MAPPINGS.font_size_headings[newSettings.font_size_headings]) {
      root.style.setProperty('--heading-scale', CSS_MAPPINGS.font_size_headings[newSettings.font_size_headings]);
    }

    // Kolor główny
    if (newSettings.primary_color) {
      root.style.setProperty('--color-primary', newSettings.primary_color);
      // Wygeneruj warianty
      root.style.setProperty('--color-primary-hover', adjustColor(newSettings.primary_color, -10));
      root.style.setProperty('--color-primary-light', adjustColor(newSettings.primary_color, 40));
    }

    // Kolor sidebar — nie ustawiaj inline, niech CSS zmienne z v3sys-theme.css
    // sterują kolorem sidebar w zależności od motywu (light/dark)
    // Inline style nadpisuje CSS variable, więc usuwamy go
    root.style.removeProperty('--sidebar-bg');

    // Gęstość UI
    if (newSettings.ui_density && CSS_MAPPINGS.ui_density[newSettings.ui_density]) {
      const density = CSS_MAPPINGS.ui_density[newSettings.ui_density];
      root.style.setProperty('--ui-padding', density.padding);
      root.style.setProperty('--ui-gap', density.gap);
      root.style.setProperty('--ui-spacing', density.spacing);
    }

    // Zaokrąglenia
    if (newSettings.border_radius && CSS_MAPPINGS.border_radius[newSettings.border_radius]) {
      root.style.setProperty('--border-radius', CSS_MAPPINGS.border_radius[newSettings.border_radius]);
    }

    // Rozmiar przycisków
    if (newSettings.button_size && CSS_MAPPINGS.button_size[newSettings.button_size]) {
      const btn = CSS_MAPPINGS.button_size[newSettings.button_size];
      root.style.setProperty('--btn-height', btn.height);
      root.style.setProperty('--btn-padding', btn.padding);
      root.style.setProperty('--btn-font-size', btn.fontSize);
    }

    // Gęstość tabel
    if (newSettings.table_density && CSS_MAPPINGS.table_density[newSettings.table_density]) {
      const table = CSS_MAPPINGS.table_density[newSettings.table_density];
      root.style.setProperty('--table-cell-padding', table.cellPadding);
      root.style.setProperty('--table-row-height', table.rowHeight);
    }

    // Animacje
    if (newSettings.animations_enabled === 'false') {
      root.style.setProperty('--transition-duration', '0s');
    } else {
      root.style.setProperty('--transition-duration', '0.2s');
    }

    // Dark mode — uzywa data-theme na <html> (v3sys-theme.css)
    if (newSettings.theme_mode === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.classList.add('dark-mode');
    } else if (newSettings.theme_mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      document.body.classList.toggle('dark-mode', prefersDark);
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      document.body.classList.remove('dark-mode');
    }

  }, []);

  // Zapisz ustawienia na serwer
  const updateSettings = useCallback(async (newSettings) => {
    try {
      await api.put('/theme-settings', newSettings);
      setSettings(prev => {
        const updated = { ...prev, ...newSettings };
        applyTheme(updated);
        return updated;
      });
      return true;
    } catch (error) {
      console.error('Błąd zapisywania ustawień:', error);
      return false;
    }
  }, [applyTheme]);

  // Reset do domyślnych
  const resetSettings = useCallback(async () => {
    try {
      const response = await api.post('/theme-settings/reset');
      if (response.data?.data) {
        setSettings(response.data.data);
        applyTheme(response.data.data);
      }
      return true;
    } catch (error) {
      console.error('Błąd resetowania ustawień:', error);
      return false;
    }
  }, [applyTheme]);

  // Załaduj ustawienia przy starcie
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Aplikuj ustawienia po załadowaniu
  useEffect(() => {
    if (!loading) {
      applyTheme(settings);
    }
  }, [loading, settings, applyTheme]);

  // Nasłuchuj zmian preferencji systemowych dla dark mode
  useEffect(() => {
    if (settings.theme_mode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = (e) => {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
        document.body.classList.toggle('dark-mode', e.matches);
      };
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [settings.theme_mode]);

  return (
    <ThemeContext.Provider value={{
      settings,
      loading,
      updateSettings,
      resetSettings,
      refreshSettings: fetchSettings
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

// Helper: Rozjaśnij/przyciemnij kolor hex
function adjustColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

export default ThemeContext;
